import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { Mail, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminNewsletters = () => {
    const { t } = useTranslation();
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [newsletters, setNewsletters] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) {
            navigate('/admin');
        }
    }, [authLoading, user, isSuperAdmin, navigate]);

    useEffect(() => {
        const fetchNewsletters = async () => {
            try {
                const response = await fetchWithSecurity(`${API_URL}newsletter.php`);
                const data = await response.json();
                if (Array.isArray(data)) {
                    setNewsletters(data);
                }
            } catch (error) {
                console.error("Failed to fetch newsletters");
            }
            setLoading(false);
        };
        if (user && isSuperAdmin()) {
            fetchNewsletters();
        }
    }, [user, isSuperAdmin]);

    const copyAllEmails = () => {
        const emails = newsletters.map(n => n.email).join(', ');
        navigator.clipboard.writeText(emails);
        toast.success(t('admin.newsletters.copied') || 'ყველა ელ.ფოსტის მისამართი დაკოპირდა!');
    };

    if (authLoading) return null;

    return (
        <AdminLayout title={t('admin.newsletters.title') || 'სიახლეების გამომწერები'}>
            <div className="shadcn-card">
                <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                    <div className="shadcn-card-title">
                        <Mail size={20} className="text-indigo" />
                        <span>{t('admin.newsletters.list') || 'გამომწერების სია'} ({newsletters.length})</span>
                    </div>
                    <button onClick={copyAllEmails} className="shadcn-btn shadcn-btn-primary shadcn-btn-sm" disabled={newsletters.length === 0}>
                        <Copy size={14} /> {t('admin.newsletters.copyAll') || 'ყველა ელ.ფოსტის კოპირება'}
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-5 text-muted">
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        <span>{t('admin.newsletters.loading') || 'გამომწერები იტვირთება...'}</span>
                    </div>
                ) : newsletters.length === 0 ? (
                    <p className="text-center text-muted py-5 mb-0">{t('admin.newsletters.empty') || 'სიახლეების გამომწერები ჯერ არ მოიძებნა.'}</p>
                ) : (
                    <div className="shadcn-table-wrapper">
                        <table className="shadcn-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t('admin.newsletters.colEmail') || 'ელექტრონული ფოსტა'}</th>
                                    <th>{t('admin.newsletters.colDate') || 'გამოწერის თარიღი'}</th>
                                    <th>{t('admin.newsletters.colStatus') || 'სტატუსი'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {newsletters.map((item, index) => (
                                    <tr key={item.id}>
                                        <td className="text-muted">#{index + 1}</td>
                                        <td className="fw-bold text-dark">{item.email}</td>
                                        <td className="small text-muted">{new Date(item.created_at).toLocaleString()}</td>
                                        <td>
                                            <span className="shadcn-badge shadcn-badge-success">
                                                <CheckCircle2 size={12} /> {t('admin.newsletters.active') || 'აქტიური გამომწერი'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default AdminNewsletters;
